// The asset manager, ported from keep_or_mull: "pick an image, or upload one"
// for whatever the caller wants it for — a post attachment, an avatar, the
// next thing. Three sections down the left — Upload a Picture, Saved Pictures
// (uploads not yet in a post), Pictures from Posts — with a thumbnail grid on
// the right. Clicking a thumbnail (or finishing an upload) enters preview
// mode: the full picture with Back / a confirm button.
//
// The modal knows nothing about what it is picking for. keep_or_mull did this
// with a singleton and setAttachAsset(callback); here the caller owns the
// `open` state and passes `onAttach`, plus `title`/`confirmLabel` to say what
// the picking is for.
//
// Rendered on the project's native <dialog> modal chrome (see modal.scss),
// so Escape, the focus trap, and the backdrop come from the browser.
import { type JSX, useEffect, useRef, useState } from "react"
import { fetchPictures, type PictureRecord, uploadPicture } from "./api"

type Section = "upload" | "saved" | "posts"

export function AssetManagerModal({
  picturesUrl,
  open,
  onClose,
  onAttach,
  title = "Your Pictures",
  confirmLabel = "Select Picture",
}: {
  picturesUrl: string
  open: boolean
  onClose: () => void
  onAttach: (picture: PictureRecord) => void
  title?: string
  confirmLabel?: string
}): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const [section, setSection] = useState<Section>("upload")
  const [pictures, setPictures] = useState<PictureRecord[]>([])
  const [preview, setPreview] = useState<PictureRecord | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const savedPictures = pictures.filter((p) => !p.in_post)
  const postPictures = pictures.filter((p) => p.in_post)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) {
      // Fresh state on every open — keep_or_mull's resetFunction.
      setSection("upload")
      setPreview(null)
      setError(null)
      void fetchPictures(picturesUrl).then((fetched) => {
        if (fetched) setPictures(fetched)
      })
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open, picturesUrl])

  async function upload(files: FileList | null): Promise<void> {
    const file = files?.[0]
    if (!file || uploading) return
    setUploading(true)
    setError(null)
    const result = await uploadPicture(picturesUrl, file)
    setUploading(false)
    if (fileInput.current) fileInput.current.value = ""

    if (typeof result === "string") {
      setError(result)
      return
    }
    // Like keep_or_mull: the fresh upload lands in Saved Pictures and jumps
    // straight to preview so one more click attaches it.
    setPictures((current) => [ result, ...current ])
    setSection("saved")
    setPreview(result)
  }

  function selectPreviewedPicture(): void {
    if (!preview) return
    onAttach(preview)
    onClose()
  }

  return (
    <dialog
      ref={dialogRef}
      className="modal discuss-assets-modal"
      onClose={onClose}
      onClick={(event) => {
        // A click on the backdrop (the dialog element itself) closes.
        if (event.target === dialogRef.current) onClose()
      }}
    >
      <div className="modal__panel">
        <div className="modal__head">
          <h2 className="modal__title">{title}</h2>
          <button type="button" className="modal__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        {error && <p className="discuss__problem" role="alert">{error}</p>}

        {preview ? (
          <div className="discuss-assets__preview">
            <button type="button" className="discuss-assets__back" onClick={() => setPreview(null)}>
              ← Back
            </button>
            <div className="discuss-assets__preview-image-container">
              {preview.url && (
                <img
                  className="discuss-assets__preview-image"
                  src={preview.url}
                  alt={preview.original_file_name ?? "Selected picture"}
                />
              )}
            </div>
            <div className="discuss-assets__preview-actions">
              <button type="button" className="btn btn--primary" onClick={selectPreviewedPicture}>
                {confirmLabel}
              </button>
            </div>
          </div>
        ) : (
          <div className="discuss-assets">
            <nav className="discuss-assets__choices" aria-label="Picture sections">
              <SectionChoice label="Upload a Picture" active={section === "upload"} onSelect={() => setSection("upload")} />
              {savedPictures.length > 0 && (
                <SectionChoice label="Saved Pictures" active={section === "saved"} onSelect={() => setSection("saved")} />
              )}
              {postPictures.length > 0 && (
                <SectionChoice label="Pictures from Posts" active={section === "posts"} onSelect={() => setSection("posts")} />
              )}
            </nav>

            <div className="discuss-assets__main">
              {section === "upload" && (
                <>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    hidden
                    onChange={(event) => void upload(event.target.files)}
                  />
                  <button
                    type="button"
                    className="discuss-assets__dropzone"
                    disabled={uploading}
                    onClick={() => fileInput.current?.click()}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault()
                      void upload(event.dataTransfer.files)
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor"
                         strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <path d="m17 8-5-5-5 5" />
                      <path d="M12 3v12" />
                    </svg>
                    {uploading ? "Uploading…" : "Upload Picture"}
                  </button>
                </>
              )}
              {section === "saved" && <PictureGrid pictures={savedPictures} onSelect={setPreview} />}
              {section === "posts" && <PictureGrid pictures={postPictures} onSelect={setPreview} />}
            </div>
          </div>
        )}
      </div>
    </dialog>
  )
}

function SectionChoice({
  label,
  active,
  onSelect,
}: {
  label: string
  active: boolean
  onSelect: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      className={`discuss-assets__choice${active ? " is-selected" : ""}`}
      aria-current={active ? "true" : undefined}
      onClick={onSelect}
    >
      {label}
    </button>
  )
}

function PictureGrid({
  pictures,
  onSelect,
}: {
  pictures: PictureRecord[]
  onSelect: (picture: PictureRecord) => void
}): JSX.Element {
  if (pictures.length === 0) {
    return <p className="discuss__note">No pictures here yet.</p>
  }
  return (
    <div className="discuss-assets__grid">
      {pictures.map((picture) => (
        <button
          key={picture.id}
          type="button"
          className="discuss-assets__thumb"
          onClick={() => onSelect(picture)}
        >
          {picture.url && (
            <img src={picture.url} alt={picture.original_file_name ?? "Picture"} loading="lazy" />
          )}
        </button>
      ))}
    </div>
  )
}
